import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { compareVersions } from 'compare-versions';
import { closeVipSubscribeDialog, getIncomeSource, isAppStorePay } from '@/utils/vip';
import { getVersion } from '@/utils/native';
import { getUrlParam, getSkuType, handleTheme } from '@/utils';
import { BusinessType, TabType } from '@/constants/subscription';
import Copywriting from './components/copywriting';
import UserinfoModule from './components/userinfo';
import ProductList from './components/product-list';
import ProductTip from './components/product-tip';
import ProductPay from './components/product-pay';
import ProductPayAppStore from './components/product-pay-appstore';
import PayResult from './components/pay-result';
import BindPhone from './components/bind-phone';
import Seats from './components/seats';
import BackIcon from '@/assets/image/vip/back.png';
import InvoicingModal from './components/invoicing';
import { message } from 'antd';
import { isTeamSubscription, SubscribeType, subscribeType } from '@/constants';
import Tabs from './components/tabs';
import Exchange from './components/exchange';
import MeidouCountInfoModal from './components/meidou-count-info';
import RedEnvelope from './components/red-envelope';
import {
  clearPollTimer,
  getProducts,
  getTransferId,
  handleRefreshQrCode,
  initActiveProductIndex,
  initFn,
} from '@/utils/subscribe';
import {
  activeIndexAtom,
  curProductAtom,
  curTabAtom,
  productsAtom,
  seatsAtom,
  visibleExchangeAtom,
  visibleInvoicingAtom,
  visibleBusinessAtom,
  businessTypeAtom,
  visibleResultAtom,
  visibleCmbChinaAtom,
  userInfoAtom,
  showNoticeBarAtom,
} from '@/store';
import { useAtom, getDefaultStore } from 'jotai';
import { track } from '@/utils/common';
import { handleLogin } from '@/utils/user';
import BusinessModal from './components/business-modal';
import CmbChina from './components/cmb-china';

import styles from './index.module.css';
import Notify from '@/components/notify';
import IconFont from '@/components/icon-font';

/** MTXX-PC：仅 7.0.0.0 及以上展示页内关闭（低版本使用宿主窗口关闭） */
const MTXX_PC_CLOSE_ICON_MIN_VERSION = '7.0.0.0';

const Vip = () => {
  const type = getUrlParam('type');

  //是否美豆弹窗类型，macos商店版本下，统一展示普通会员类型场景
  const useMeidou = subscribeType === SubscribeType.MEIDOU && !isAppStorePay();

  const [curTab, setCurTab] = useAtom(curTabAtom);
  const [products, setProducts] = useAtom(productsAtom);
  const [, setActiveIndex] = useAtom(activeIndexAtom);
  const [curProduct] = useAtom(curProductAtom);
  const [visibleExchange, setVisibleExchange] = useAtom(visibleExchangeAtom);
  const [userInfo] = useAtom(userInfoAtom);
  const [, setVisibleInvoicing] = useAtom(visibleInvoicingAtom);
  const [, setVisibleBusiness] = useAtom(visibleBusinessAtom);
  const [, setBusinessType] = useAtom(businessTypeAtom);
  const [visibleResult] = useAtom(visibleResultAtom);
  const [seats] = useAtom(seatsAtom);
  const [, setVisibleCmbChina] = useAtom(visibleCmbChinaAtom);
  const [showNoticeBar] = useAtom(showNoticeBarAtom);

  const [hasInited, setHasInited] = useState<boolean>(false);
  const [showPcCloseIcon, setShowPcCloseIcon] = useState(false);

  // 我要开票
  const handleJump = () => {
    let params = {};
    if (getIncomeSource() === 'MTXX-WEB') {
      params = {
        class: '我要开票',
        name_id: curProduct?.productId,
        country_code: curProduct?.countryCode || '',
      };
    } else {
      params = {
        category: '我要开票',
        product_id: curProduct?.productId,
        country_code: curProduct?.countryCode || '',
      };
    }

    track({
      event_id: 'vip_page_click',
      event_name: '会员落地页点击',
      params,
    });
    if (!userInfo?.id) {
      message.warning('请先登录');
      return;
    }
    setVisibleInvoicing(true);
  };

  const handleJumpLogin = () => {
    if (getIncomeSource() !== 'MTXX-WEB') {
      track({
        event_id: 'vip_page_click',
        event_name: '会员落地页点击',
        params: {
          category: '点击登录',
          product_id: curProduct?.productId || '',
          country_code: curProduct?.countryCode || '',
        },
      });
    }

    handleLogin();
  };

  const handleClickExchangeEntry = () => {
    let params = {};
    if (getIncomeSource() === 'MTXX-WEB') {
      params = {
        class: '兑换码开通',
        name_id: curProduct?.productId,
        country_code: curProduct?.countryCode || '',
      };
    } else {
      params = {
        category: '兑换码开通',
        product_id: curProduct?.productId,
        country_code: curProduct?.countryCode || '',
      };
    }

    track({
      event_id: 'vip_page_click',
      event_name: '会员落地页点击',
      params,
    });

    if (!userInfo?.id) {
      handleLogin();
      return;
    }
    setVisibleExchange(true);
  };

  useEffect(() => {
    if (products.length) {
      // 初始化商品默认选中状态
      initActiveProductIndex();
    }
  }, [products]);

  useEffect(() => {
    handleTheme();
    initFn(() => {
      setHasInited(true);
    });

    return () => {
      // 页面销毁时，清除定时器
      clearPollTimer();
    };
  }, []);

  useEffect(() => {
    if (getIncomeSource() !== 'MTXX-PC') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const version = await getVersion();
        if (cancelled || !version) return;
        // compareVersions: 相等时返回0，小于时返回1，大于时返回-1
        if (compareVersions(MTXX_PC_CLOSE_ICON_MIN_VERSION, version) <= 0) {
          setShowPcCloseIcon(true);
        }
      } catch {
        // 取不到版本时不展示页内关闭
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // 切换商品、切换席位数，需要重新刷新支付二维码
    if (curProduct) {
      handleRefreshQrCode();
    }
  }, [curProduct, seats]);

  useEffect(() => {
    if (hasInited) {
      setProducts([]);
      setActiveIndex(-1);
      getProducts();
    }

    // web订阅触发页面进入事件
    if (getIncomeSource() === 'MTXX-WEB') {
      track({
        event_id: 'vip_page_enter',
        params: {
          sku_type: getSkuType(),
        },
      });
    }
  }, [curTab]);

  const messageHandler = (e: any) => {
    let data = e.data || (e.originalEvent && e.originalEvent.data);
    if (typeof data === 'string' && /^[\[\{]/.test(data)) {
      data = JSON.parse(data);
    }

    if (data && data.action === 'viewSubscription') {
      // 修改URL上的entrance参数值
      const url = new URL(window.location.href);
      const params = url.searchParams;
      params.set('entrance', 'retention_popup');
      window.history.replaceState({}, '', url);
      // 切换成购买会员
      setCurTab(TabType.SUB);

      track({
        event_id: 'vip_page_enter',
        params: {},
      });
    }
  };

  useEffect(() => {
    window.addEventListener('message', messageHandler, false);
    return () => {
      window.removeEventListener('message', messageHandler, false);
    };
  }, []);

  const renderOther = () => (
    <div className={styles.others}>
      {isTeamSubscription ? (
        <>
          {curTab === TabType.SUB ? (
            <>
              <div
                onClick={() => {
                  setVisibleCmbChina(true);
                  track({
                    event_id: 'vip_page_click',
                    event_name: '会员落地页点击',
                    params: {
                      class: 'corporate_payment',
                    },
                  });
                }}
              >
                对公转账
              </div>
              <div className={styles.separater}></div>
            </>
          ) : null}

          <div
            onClick={() => {
              setVisibleBusiness(true);
              setBusinessType(BusinessType.LargeAmount);
            }}
          >
            联系商务
          </div>
        </>
      ) : null}
      {useMeidou && curTab === TabType.MEIDOU ? (
        <>
          {isTeamSubscription ? <div className={styles.separater}></div> : null}
          <div onClick={() => window.open('https://www.designkit.cn/meidou-introduce')}>
            常见问题
          </div>
          {!isTeamSubscription ? <div className={styles.separater}></div> : null}
        </>
      ) : null}
      {isTeamSubscription ? null : <div onClick={handleJump}>我要开票</div>}
      {!isAppStorePay() && // Mac应用商店版不展示
      type !== 'svip' && // 批处理不展示
      !isTeamSubscription && // 团队版不展示
      [TabType.SUB, TabType.PRO_SUB].includes(curTab) ? (
        <>
          <div className={styles.separater}></div>
          <div onClick={handleClickExchangeEntry}>邀请码/兑换码开通</div>
        </>
      ) : null}
    </div>
  );

  const renderProductModule = () => (
    <div className={styles.purchaseModule}>
      <UserinfoModule handleLogin={handleJumpLogin} />

      {/* web端个人版下，展示限时电商节公告 */}
      {showNoticeBar ? <Notify text="3月限时电商节，首充赠送双倍美豆" /> : null}

      <Tabs></Tabs>

      <div className={styles.products}>
        <ProductList useMeidou={useMeidou} onRefresh={getProducts} />
      </div>
      {curTab === TabType.SUB && isTeamSubscription ? null : (
        <div className={styles.productTip}>
          <ProductTip
            isMeidouTab={curTab === TabType.MEIDOU && useMeidou}
            onRefresh={getProducts}
          />
        </div>
      )}

      <Seats />
      <div className={styles.pay}>
        {isAppStorePay() ? (
          // Mac商店版本
          <ProductPayAppStore handleLogin={handleJumpLogin}>{renderOther()}</ProductPayAppStore>
        ) : (
          // 非Mac商店版本
          <ProductPay
            handleLogin={handleJumpLogin}
            onRefresh={handleRefreshQrCode}
            useMeidou={curTab === TabType.MEIDOU && useMeidou}
          >
            {renderOther()}
          </ProductPay>
        )}
      </div>
    </div>
  );

  const renderExchange = () => (
    <div className={styles.exchangeModule}>
      <div className={styles.exchangeNav}>
        <div className={styles.exchangeNavBack} onClick={() => setVisibleExchange(false)}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M10.8737 14.0403C11.2642 13.6498 11.2642 13.0166 10.8737 12.6261L6.24745 7.99984L10.8737 3.37361C11.2642 2.98309 11.2642 2.34992 10.8737 1.9594C10.4831 1.56887 9.84998 1.56887 9.45946 1.9594L4.59753 6.82133C3.94666 7.4722 3.94666 8.52747 4.59753 9.17835L9.45946 14.0403C9.84998 14.4308 10.4831 14.4308 10.8737 14.0403Z"
              fill="#86888C"
            />
          </svg>
          <span>返回购买</span>
        </div>
      </div>
      <Exchange onRefresh={getProducts} />
    </div>
  );

  return (
    <div
      className={classnames(styles.main, { [styles.meidou]: useMeidou })}
      style={{
        borderRadius: getIncomeSource() !== 'MTXX-WEB' ? 0 : '',
        width: getIncomeSource() !== 'MTXX-WEB' ? '100%' : '',
        height: getIncomeSource() !== 'MTXX-WEB' ? '100%' : '',
      }}
    >
      <Copywriting />
      <div className={classnames(styles.right, 'contentBox')}>
        {!visibleExchange ? renderProductModule() : renderExchange()}
      </div>
      {visibleResult ? <PayResult /> : null}

      {/* 绑定手机号弹窗 */}
      <BindPhone />

      {/* 开票弹窗 */}
      <InvoicingModal />

      {/* 联系商务弹窗 */}
      <BusinessModal />

      {/* 美豆余额弹窗 */}
      <MeidouCountInfoModal />

      {/* 红包弹窗 */}
      <RedEnvelope />

      {/* 对公转账弹窗 */}
      {isTeamSubscription && curTab === TabType.SUB && curProduct?.productId ? (
        <CmbChina transferId={getTransferId()} />
      ) : null}

      {/* MTXX-PC 7.0.0.0+：黑金主题下宿主关闭不可见时，展示页内关闭 */}
      {showPcCloseIcon ? (
        <img
          className={styles.pcCloseIcon}
          src="https://public.static.meitudata.com/xiuxiu-pc/saas-subscription/close-icon.png"
          alt=""
          onClick={() => {
            closeVipSubscribeDialog({
              success: false,
            });
          }}
        />
      ) : null}
    </div>
  );
};

export default Vip;
